import { FieldError } from '@components/field-error';
import { Input, InputProps, InputRef } from 'antd';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { forwardRef, Ref } from 'react';

type Props<T extends FieldValues> = InputProps & {
  required?: boolean;
  label?: string | React.ReactNode;
  name: Path<T>;
  placeholder?: string;
  control: Control<T>;
  suffixLabel?: string;
};

function AppInputInner<T extends FieldValues>(props: Props<T>, ref: Ref<InputRef>) {
  const { required, label, name, placeholder, suffixLabel, control, ...restProps } = props;
  const inputTitle = typeof label === 'string' ? label : undefined;

  return (
    <div className="flex flex-col gap-0.5 md:gap-1.5 w-full">
      {label && (
        <p className="text-base leading-5	font-medium	text-text-800">
          {label}
          <span className="text-text-400 text-xs font-normal">{suffixLabel && suffixLabel}</span>{' '}
          {required && <span className="text-red-500">*</span>}
        </p>
      )}

      <Controller
        name={name}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <div className="w-full flex flex-col gap-0.5 md:gap-1.5">
            <Input
              ref={ref}
              size="large"
              status={error && 'error'}
              title={inputTitle}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              {...restProps}
            />
            <FieldError message={error?.message} />
          </div>
        )}
      />
    </div>
  );
}

export const AppInput = forwardRef(AppInputInner) as <T extends FieldValues>(
  props: Props<T> & { ref?: Ref<InputRef> }
) => ReturnType<typeof AppInputInner>;
